import { useMemo } from 'react';
import { ModalContextType } from '@lp/features/RoutePlanner/types';
import { useRouteBuilderState } from '@lp/features/RoutePlanner/store';

type UseRouteModalsType = {
  data: ModalContextType;
  isLoading: boolean;
  isOpen: boolean;
  setIsLoading: (bool: boolean) => void;
  onOpenModal: (context: ModalContextType) => void;
  onCloseModal: () => void;
};

type UseRouteModalActionsType = Pick<UseRouteModalsType, 'setIsLoading' | 'onOpenModal' | 'onCloseModal'>;

// Actions-only view of the modal store: subscribes to no state slices, so callers
// do not re-render when a modal opens, closes, or toggles its loading flag.
export const useRouteModalActions = (): UseRouteModalActionsType => {
  const setIsLoading = useRouteBuilderState.use.setModalLoading();
  const onOpenModal = useRouteBuilderState.use.handleOpenModal();
  const onCloseModal = useRouteBuilderState.use.handleCloseModal();

  return { setIsLoading, onOpenModal, onCloseModal };
};

export const useRouteModals = (): UseRouteModalsType => {
  const isLoading = useRouteBuilderState.use.isModalLoading();
  const data = useRouteBuilderState.use.modal();
  const isOpen = useRouteBuilderState.use.isModalOpen();
  const { setIsLoading, onOpenModal, onCloseModal } = useRouteModalActions();

  return useMemo(
    () => ({
      data,
      isOpen,
      isLoading,
      setIsLoading,
      onOpenModal,
      onCloseModal,
    }),
    [data, isOpen, isLoading, setIsLoading, onOpenModal, onCloseModal],
  );
};
